(function () {
  'use strict';
  function createLookController(root, options = {}) {
    const panels = Array.from(root.querySelectorAll('[data-look-panel]'));
    if (!panels.length) return { destroy() {} };
    const controls = root.querySelector('[data-look-controls]');
    const counter = root.querySelector('[data-look-counter]');
    const status = root.querySelector('[data-look-status]');
    const playback = root.querySelector('[data-look-autoplay]');
    const page = root.ownerDocument;
    const motion = options.motionMedia || (typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null);
    const Observer = options.IntersectionObserver || (typeof IntersectionObserver !== 'undefined' ? IntersectionObserver : null);
    const setTimer = options.setTimeout || setTimeout;
    const clearTimer = options.clearTimeout || clearTimeout;
    let active = 0, pointer = null, touch = null, timer = null, visible = false, paused = false, destroyed = false;
    let observedImage = null, transition = null;
    const finishTransition = () => {
      if (!transition) return;
      const previous = transition;
      transition = null;
      previous.animations.forEach(animation => animation.cancel());
      previous.panel.hidden = true;
      previous.panel.classList.remove('BFCompleteLook__Panel--leaving');
    };
    const slide = (previous, next, direction) => {
      const outgoing = previous.querySelector('[data-look-image]');
      const incoming = next.querySelector('[data-look-image]');
      if (motion?.matches || !outgoing?.animate || !incoming?.animate) return;
      previous.hidden = false;
      previous.classList.add('BFCompleteLook__Panel--leaving');
      const current = { panel: previous, animations: [] };
      transition = current;
      const timing = { duration: 480, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' };
      current.animations.push(outgoing.animate([
        { transform: 'translateX(0)' }, { transform: `translateX(${-direction * 100}%)` }
      ], timing));
      const arrival = incoming.animate([
        { transform: `translateX(${direction * 100}%)` }, { transform: 'translateX(0)' }
      ], timing);
      current.animations.push(arrival);
      arrival.onfinish = () => { if (transition === current) finishTransition(); };
    };
    const clear = () => { if (timer !== null) clearTimer(timer); timer = null; };
    const schedule = () => {
      clear();
      const reduced = Boolean(motion?.matches);
      const running = panels.length > 1 && !paused && !reduced && visible && !page?.hidden && !destroyed;
      root.dataset.autoplay = running ? 'playing' : 'paused';
      if (playback) {
        playback.hidden = panels.length < 2 || reduced;
        playback.setAttribute('aria-label', paused ? 'Play outfits' : 'Pause outfits');
        playback.setAttribute('aria-pressed', String(!paused));
      }
      if (running) timer = setTimer(() => { timer = null; show(active + 1, false); }, 4000);
    };
    const pause = () => { paused = true; schedule(); };
    const visibility = Observer ? new Observer(entries => {
      entries.forEach(entry => {
        if (entry.target === observedImage) {
          visible = entry.isIntersecting && entry.intersectionRatio >= 0.3;
          schedule();
        }
      });
    }, { threshold: [0, 0.3] }) : null;
    const show = (index, announce = true) => {
      finishTransition();
      const next = (index % panels.length + panels.length) % panels.length;
      const previous = active;
      active = next;
      panels.forEach((panel, position) => { panel.hidden = position !== active; panel.inert = position !== active; });
      if (previous !== active && !destroyed) slide(panels[previous], panels[active], index > previous ? 1 : -1);
      root.dataset.activeLook = String(active + 1);
      if (counter) counter.textContent = `${active + 1} / ${panels.length}`;
      if (status && announce) status.textContent = `Look ${active + 1} of ${panels.length}: ${panels[active].dataset.lookTitle}`;
      const image = panels[active].querySelector('[data-look-image]');
      if (image !== observedImage) {
        if (observedImage) visibility?.unobserve(observedImage);
        observedImage = image;
        visible = false;
        if (image) visibility?.observe(image);
      }
      schedule();
    };
    const onClick = event => {
      if (event.target.closest('[data-look-autoplay]')) { paused = !paused; schedule(); return; }
      pause();
      const step = event.target.closest('[data-look-step]');
      if (step && root.contains(step)) show(active + Number(step.dataset.lookStep));
    };
    const onKey = event => {
      const playbackKey = event.target.closest('[data-look-autoplay]') && ['Enter', ' '].includes(event.key);
      if (!playbackKey) pause();
      if (!event.target.closest('[data-look-controls]')) return;
      const index = { ArrowLeft: active - 1, ArrowRight: active + 1, Home: 0, End: panels.length - 1 }[event.key];
      if (index !== undefined) { event.preventDefault(); show(index); }
    };
    const onInteract = event => { if (!event.target.closest('[data-look-autoplay]')) pause(); };
    const onWheel = event => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY) && event.target.closest('[data-look-products]')) pause();
    };
    const checkTouchScroll = () => {
      if (touch && Math.abs(touch.rail.scrollLeft - touch.left) > 2) pause();
    };
    const cancelTouch = () => { checkTouchScroll(); touch = null; };
    const onTouchStart = event => {
      touch = null;
      const rail = event.target.closest('[data-look-products]');
      if (rail && event.touches.length === 1) {
        const start = event.touches[0];
        touch = { id: start.identifier, rail, left: rail.scrollLeft };
      }
    };
    const onTouchMove = event => {
      if (!touch) return;
      if (event.touches.length !== 1) { cancelTouch(); return; }
      const current = event.touches[0];
      if (current.identifier !== touch.id) { cancelTouch(); return; }
      checkTouchScroll();
    };
    const onScroll = event => { if (touch && event.target === touch.rail) checkTouchScroll(); };
    const onDown = event => {
      const image = event.target.closest('[data-look-image]');
      const products = event.target.closest('[data-look-products]');
      if (products && event.pointerType === 'mouse') pause();
      if (event.pointerType === 'mouse' || (!image && !products) || (!products && event.target.closest('a, button'))) return;
      pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, image: Boolean(image) };
    };
    const onUp = event => {
      if (!pointer || event.pointerId !== pointer.id) return;
      const dx = event.clientX - pointer.x, dy = event.clientY - pointer.y;
      const image = pointer.image;
      pointer = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        pause();
        if (image) show(active + (dx < 0 ? 1 : -1));
      }
    };
    const cancel = () => { pointer = null; };
    const onMotion = () => { if (motion?.matches) finishTransition(); schedule(); };
    const onBlockSelect = event => {
      const index = panels.findIndex(panel => panel.dataset.lookBlock === event.detail?.blockId);
      if (index >= 0) { pause(); show(index, false); }
    };
    const events = { click: onClick, keydown: onKey, focusin: onInteract, wheel: onWheel, scroll: onScroll, touchstart: onTouchStart, touchmove: onTouchMove, touchend: cancelTouch, touchcancel: cancelTouch, pointerdown: onDown, pointerup: onUp, pointercancel: cancel, 'shopify:block:select': onBlockSelect };
    Object.entries(events).forEach(([type, handler]) => root.addEventListener(type, handler, { capture: type === 'scroll', passive: type === 'wheel' || type.startsWith('touch') }));
    page?.addEventListener('visibilitychange', schedule);
    motion?.addEventListener('change', onMotion);
    if (controls) controls.hidden = panels.length < 2;
    show(0, false);
    return { show, destroy() {
      destroyed = true; clear(); finishTransition(); visibility?.disconnect();
      page?.removeEventListener('visibilitychange', schedule); motion?.removeEventListener('change', onMotion);
      Object.entries(events).forEach(([type, handler]) => root.removeEventListener(type, handler, { capture: type === 'scroll' }));
    } };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { createLookController };
  if (typeof customElements !== 'undefined' && !customElements.get('bf-complete-the-look')) {
    customElements.define('bf-complete-the-look', class extends HTMLElement {
      connectedCallback() {
        if (!this.lookController) this.lookController = createLookController(this);
        if (!this.productRails) this.productRails = Array.from(this.querySelectorAll('[data-look-panel]')).map(panel => window.createBfLookProductRail(panel));
      }
      disconnectedCallback() {
        this.lookController?.destroy(); this.lookController = null;
        this.productRails?.forEach(rail => rail.destroy()); this.productRails = null;
      }
    });
  }
}());
